import { footerVisual } from "@/data/footerVisual";

/**
 * Cinematic image strip for the footer: a real Shingare application photo, deliberately kept at native resolution.
 * A 648 px photo cannot fill a 1240 px strip sharply, so it is layered:
 *   1. the same photo, scaled up, heavily blurred and navy-tinted — ambient colour that fills the full width;
 *   2. the sharp photo on the right, feathered into (1) on both sides;
 *   3. a left-to-right navy gradient (keeps the copy legible and the machinery on the right clear) and a faint blue glow.
 * One image request serves both layers. Decorative copy sits on the left; nothing covers the equipment.
 */
export default function FooterIndustrialVisual({ visual = footerVisual }) {
  if (!visual?.image) return null;
  const { image, width, height, alt, eyebrow, description, position } = visual;
  return (
    <section
      aria-label="Shingare industrial equipment in operation"
      data-testid="footer-industrial-visual"
      className="relative isolate mt-12 h-[168px] overflow-hidden rounded-[20px] border border-white/10 bg-[#071B3A] shadow-[0_30px_80px_-40px_rgba(23,105,255,0.55)] sm:h-[190px] lg:h-[220px]"
    >
      {/* 1 — ambient fill */}
      <img src={image} alt="" aria-hidden="true" width={width} height={height} loading="lazy" decoding="async" draggable="false" className="absolute inset-0 -z-20 h-full w-full scale-125 object-cover opacity-60 blur-[26px]" />
      <div className="absolute inset-0 -z-10 bg-[#050F24]/45" aria-hidden="true" />

      {/* 2 — the photograph itself, sharp */}
      <div className="absolute inset-y-0 right-0 w-full md:w-[62%] lg:w-[54%]">
        <img
          src={image}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          draggable="false"
          className="h-full w-full object-cover [-webkit-mask-image:linear-gradient(90deg,transparent_0,#000_26%,#000_94%,transparent_100%)] [mask-image:linear-gradient(90deg,transparent_0,#000_26%,#000_94%,transparent_100%)] max-md:[object-position:var(--pos-base)] md:[object-position:var(--pos-md)]"
          style={{ "--pos-base": position?.base || "50% 4%", "--pos-md": position?.md || "50% 3%" }}
        />
        <div className="absolute inset-0 bg-[#071B3A]/30" aria-hidden="true" />
      </div>

      {/* 3 — legibility gradient, blue ambient glow, hairline highlight */}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,15,36,0.94)_0%,rgba(5,15,36,0.72)_30%,rgba(5,15,36,0.12)_60%,rgba(5,15,36,0.25)_100%)] max-md:bg-[linear-gradient(180deg,rgba(5,15,36,0)_62%,rgba(5,15,36,0.9)_100%)]" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-10 -bottom-16 h-56 w-72 rounded-full bg-[#1769FF]/25 blur-[70px]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" aria-hidden="true" />

      {(eyebrow || description) && (
        <div className="absolute inset-y-0 left-0 flex max-w-[420px] flex-col justify-center px-6 sm:px-8 md:max-w-[330px] lg:max-w-[460px] lg:px-10 max-md:inset-x-0 max-md:top-auto max-md:justify-end max-md:px-5 max-md:pb-3.5">
          {eyebrow && (
            <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#7FB0FF]">
              <span className="h-px w-8 bg-[#1769FF]" aria-hidden="true" />
              {eyebrow}
            </div>
          )}
          {/* phones: eyebrow only — a caption would sit on top of the equipment */}
          {description && <p className="mt-3 hidden max-w-[340px] text-[14px] leading-relaxed text-white/75 md:block md:text-[15px]">{description}</p>}
        </div>
      )}
    </section>
  );
}
